import { getRouteApi, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Cluster,
  ErrorState,
  Icon,
  Icons,
  LoadingState,
  PageHeader,
  Panel,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useScopedWorkspace } from '../lib/operatorWorkspace';
import { useOperationsStream } from '../lib/useOperationsStream';
import { RunStatusBadge } from '../components/RunStatusBadge';
import { RunTranscript } from '../components/RunTranscript';

const runTranscriptRoute = getRouteApi('/app/runs/$runId/transcript');

/**
 * Run transcript — the "advanced" step-by-step view (§14, issue #120): model turns,
 * tool calls with args/results, approvals, and wakes. A full-screen route split off the
 * run page so that page can lead with Outputs; a run's Outputs are the product, the
 * transcript is the power-user audit detail.
 *
 * Live via SSE: `useOperationsStream` invalidates `qk.run(ws, runId)` on each
 * `run_update`, refetching the first page of steps; `RunTranscript` then keyset-
 * paginates the rest on demand.
 */
export function RunTranscriptPage() {
  // #278: scope to the operator-selected workspace (`ws` carried through the run pages),
  // defaulting to the caller's own workspace. SSE stays own-workspace-only (foreign =
  // non-live) so an operator's cross-tenant transcript view never crashes on the stream.
  const { ws } = runTranscriptRoute.useSearch();
  const { workspaceId, viewOnly } = useScopedWorkspace(ws);
  const { runId } = useParams({ from: '/app/runs/$runId/transcript' });
  const navigate = useNavigate();
  useOperationsStream(viewOnly ? null : workspaceId);

  const run = useQuery({
    queryKey: qk.run(workspaceId, runId),
    queryFn: () => api.getRun(workspaceId, runId),
  });

  if (run.isLoading) return <LoadingState variant="skeleton" rows={6} label="Loading transcript…" />;
  if (run.isError) {
    return (
      <ErrorState
        description={run.error instanceof ApiError ? run.error.message : 'Could not load this run.'}
        onRetry={() => void run.refetch()}
      />
    );
  }

  const detail = run.data;
  if (!detail) return null;

  return (
    <>
      <PageHeader
        title={`Transcript · Run ${detail.run.id.slice(0, 8)}`}
        description={detail.run.progressSummary ?? undefined}
        actions={
          <Cluster gap="3">
            <RunStatusBadge status={detail.run.status} />
            <Button
              variant="secondary"
              size="sm"
              iconStart={<Icon as={Icons.ArrowLeft} size="sm" />}
              // #279: the transcript is opened from the full-page run detail, so Back
              // returns there (not to the split-view preview), carrying the runs filters.
              onClick={() =>
                void navigate({
                  to: '/app/runs/$runId/full',
                  params: { runId },
                  search: (prev) => prev,
                })
              }
              data-testid="back-to-run"
            >
              Back to run
            </Button>
          </Cluster>
        }
      />

      <Panel title="Steps">
        <RunTranscript
          workspaceId={workspaceId}
          runId={runId}
          initialSteps={detail.steps}
          initialCursor={detail.nextStepCursor}
        />
      </Panel>
    </>
  );
}
